import { useState, useMemo, useEffect } from "react";
import { X, Plus, Trash2, FileText, Settings2, Wand2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Modal Pro pour créer un devis structuré. Toutes les infos du pro sont pré-remplies
 * (nom, société, SIRET, téléphone, email, adresse, logo, RGE). Un modèle de prestation
 * par métier peut être injecté en 1 clic. Le pro peut aussi personnaliser : acompte,
 * IBAN, conditions de règlement, adresse & date d'intervention, mentions personnelles.
 */
export default function QuoteBuilder({ open, onOpenChange, leadId, leadTitle, onCreated }) {
  const { user } = useAuth();
  const [items, setItems] = useState([
    { description: "", quantity: 1, unit: "u", unit_price_ht: 0 },
  ]);
  const [tvaRate, setTvaRate] = useState(20);
  const [validDays, setValidDays] = useState(30);
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [acomptePct, setAcomptePct] = useState("");
  const [iban, setIban] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("Paiement à réception – 15 jours");
  const [interventionAddress, setInterventionAddress] = useState("");
  const [interventionDate, setInterventionDate] = useState("");
  const [customFooter, setCustomFooter] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);

  const totals = useMemo(() => {
    const ht = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price_ht) || 0), 0);
    const tva = ht * (Number(tvaRate) || 0) / 100;
    const ttc = ht + tva;
    const acAmount = acomptePct ? ttc * (Number(acomptePct) || 0) / 100 : 0;
    return {
      ht: Math.round(ht * 100) / 100,
      tva: Math.round(tva * 100) / 100,
      ttc: Math.round(ttc * 100) / 100,
      acompte: Math.round(acAmount * 100) / 100,
    };
  }, [items, tvaRate, acomptePct]);

  useEffect(() => {
    if (!open) return;
    // reset when opening fresh
    setItems([{ description: "", quantity: 1, unit: "u", unit_price_ht: 0 }]);
    setNotes(""); setAcomptePct(""); setIban(""); setInterventionAddress("");
    setInterventionDate(""); setCustomFooter(""); setShowOptions(false);
  }, [open]);

  if (!open) return null;

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((arr) => [...arr, { description: "", quantity: 1, unit: "u", unit_price_ht: 0 }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const applyTemplate = async () => {
    setTemplateLoading(true);
    try {
      const { data } = await api.get("/quotes/templates");
      setItems(data.items.map((t) => ({ ...t })));
      toast.success(`Modèle « ${data.trade || "générique"} » appliqué`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTemplateLoading(false); }
  };

  const submit = async () => {
    const clean = items
      .filter((i) => i.description.trim() && Number(i.unit_price_ht) > 0)
      .map((i) => ({
        description: i.description.trim(),
        quantity: Number(i.quantity) || 1,
        unit: i.unit || "u",
        unit_price_ht: Number(i.unit_price_ht),
      }));
    if (clean.length === 0) {
      toast.error("Ajoutez au moins une ligne avec description et prix");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/quotes", {
        lead_id: leadId,
        items: clean,
        tva_rate: Number(tvaRate) || 20,
        valid_days: Number(validDays) || 30,
        notes: notes.trim() || undefined,
        acompte_pct: acomptePct ? Number(acomptePct) : undefined,
        iban: iban.trim() || undefined,
        payment_terms: paymentTerms.trim() || undefined,
        intervention_address: interventionAddress.trim() || undefined,
        intervention_date: interventionDate.trim() || undefined,
        custom_footer: customFooter.trim() || undefined,
      });
      toast.success(`Devis ${data.number} envoyé au client`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const ident = user?.siret || user?.identifier || user?.siren;
  const identType = (user?.identifier_type || (user?.siret ? "siret" : user?.siren ? "siren" : "siret")).toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="quote-builder">
      <div className="bg-white w-full sm:max-w-md sm:mx-4 sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white px-5 pt-4 pb-3 border-b border-border flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-pro/10 text-pro flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading font-bold text-ink">Nouveau devis</div>
              <div className="text-[11px] text-slate2 truncate max-w-[220px]">{leadTitle}</div>
            </div>
          </div>
          <button onClick={() => onOpenChange(false)} className="tap-scale text-slate2" data-testid="quote-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Bloc infos pro pré-remplies */}
          <div className="bg-bone rounded-2xl border border-border p-3 flex items-start gap-3">
            {user?.logo && (
              <img src={user.logo} alt="logo" className="w-12 h-12 rounded-xl object-cover border border-border shrink-0" data-testid="quote-pro-logo" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate2 font-semibold mb-1">Émetteur (pré-rempli)</div>
              <div className="text-sm font-heading font-bold text-ink">{user?.company || user?.name}</div>
              <div className="text-xs text-slate2 leading-relaxed">
                {user?.name}
                {user?.city ? ` · ${user.area ? user.area + ", " : ""}${user.city}` : ""}<br />
                {ident ? `${identType} ${ident}` : ""}
                {user?.phone ? ` · ${user.phone}` : ""}<br />
                {user?.email}
                {user?.rge_verified ? <span className="text-forest font-semibold"> · RGE ✓</span> : null}
              </div>
            </div>
          </div>

          {/* Modèle par métier */}
          <button
            onClick={applyTemplate}
            disabled={templateLoading}
            className="w-full rounded-xl border border-dashed border-brand/40 bg-brand-light/60 text-brand text-sm font-semibold py-2.5 flex items-center justify-center gap-2 tap-scale"
            data-testid="quote-apply-template-btn"
          >
            <Wand2 className="w-4 h-4" />
            {templateLoading ? "Chargement…" : "Pré-remplir avec le modèle de mon métier"}
          </button>

          {/* Items */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate2 font-semibold mb-2">Prestations</div>
            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-border p-3 space-y-2" data-testid={`quote-item-${idx}`}>
                  <Input
                    value={it.description}
                    onChange={(e) => updateItem(idx, { description: e.target.value })}
                    placeholder="Description (ex: Pose robinetterie évier cuisine)"
                    className="rounded-xl text-sm"
                    data-testid={`quote-item-desc-${idx}`}
                  />
                  <div className="grid grid-cols-12 gap-2">
                    <Input
                      type="number" min="0" step="0.5"
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                      placeholder="Qté"
                      className="rounded-xl text-sm col-span-3"
                      data-testid={`quote-item-qty-${idx}`}
                    />
                    <Input
                      value={it.unit}
                      onChange={(e) => updateItem(idx, { unit: e.target.value })}
                      placeholder="Unité"
                      className="rounded-xl text-sm col-span-3"
                      data-testid={`quote-item-unit-${idx}`}
                    />
                    <Input
                      type="number" min="0" step="0.01"
                      value={it.unit_price_ht}
                      onChange={(e) => updateItem(idx, { unit_price_ht: e.target.value })}
                      placeholder="Prix HT"
                      className="rounded-xl text-sm col-span-4"
                      data-testid={`quote-item-price-${idx}`}
                    />
                    <button
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="col-span-2 h-9 rounded-xl border border-border text-slate2 flex items-center justify-center disabled:opacity-30 tap-scale"
                      data-testid={`quote-item-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-[11px] text-slate2 text-right">
                    Sous-total HT : <span className="font-semibold text-ink">
                      {((Number(it.quantity) || 0) * (Number(it.unit_price_ht) || 0)).toFixed(2)} €
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={addItem}
              className="mt-2 w-full h-10 border-2 border-dashed border-border rounded-xl text-sm text-slate2 flex items-center justify-center gap-1.5 tap-scale hover:border-brand hover:text-brand"
              data-testid="quote-add-item-btn"
            >
              <Plus className="w-4 h-4" /> Ajouter une ligne
            </button>
          </div>

          {/* Réglages */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">TVA (%)</label>
              <Input
                type="number" min="0" step="0.5"
                value={tvaRate}
                onChange={(e) => setTvaRate(e.target.value)}
                className="rounded-xl mt-1"
                data-testid="quote-tva-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Validité (jours)</label>
              <Input
                type="number" min="1"
                value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
                className="rounded-xl mt-1"
                data-testid="quote-valid-days-input"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Notes / conditions</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Conditions particulières, délai d'intervention, acompte demandé…"
              className="rounded-xl mt-1"
              data-testid="quote-notes-input"
            />
          </div>

          {/* Options avancées */}
          <button
            onClick={() => setShowOptions((s) => !s)}
            className="w-full flex items-center justify-between text-xs uppercase tracking-widest text-slate2 font-semibold border-t border-border pt-3"
            data-testid="quote-toggle-options-btn"
          >
            <span className="flex items-center gap-1.5"><Settings2 className="w-3.5 h-3.5" /> Options avancées</span>
            <span>{showOptions ? "−" : "+"}</span>
          </button>
          {showOptions && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Acompte (%)</label>
                  <Input
                    type="number" min="0" max="100"
                    value={acomptePct}
                    onChange={(e) => setAcomptePct(e.target.value)}
                    placeholder="ex: 30"
                    className="rounded-xl mt-1"
                    data-testid="quote-acompte-input"
                  />
                  {totals.acompte > 0 && (
                    <div className="text-[10px] text-slate2 mt-1">= {totals.acompte.toFixed(2)} € TTC</div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Conditions</label>
                  <Input
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    placeholder="À réception, 30j, etc."
                    className="rounded-xl mt-1"
                    data-testid="quote-payment-terms-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">IBAN (règlement)</label>
                <Input
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  placeholder="FR76 …"
                  className="rounded-xl mt-1"
                  data-testid="quote-iban-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Adresse intervention</label>
                  <Input
                    value={interventionAddress}
                    onChange={(e) => setInterventionAddress(e.target.value)}
                    placeholder="ex: 12 rue…"
                    className="rounded-xl mt-1"
                    data-testid="quote-address-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Date prévue</label>
                  <Input
                    value={interventionDate}
                    onChange={(e) => setInterventionDate(e.target.value)}
                    placeholder="ex: 15/03/2026"
                    className="rounded-xl mt-1"
                    data-testid="quote-date-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Mentions personnelles</label>
                <Textarea
                  value={customFooter}
                  onChange={(e) => setCustomFooter(e.target.value)}
                  rows={2}
                  placeholder="Garantie décennale, assurance RC Pro, mention 'Bon pour accord'…"
                  className="rounded-xl mt-1"
                  data-testid="quote-footer-input"
                />
              </div>
            </div>
          )}

          {/* Totaux */}
          <div className="bg-pro text-white rounded-2xl p-4 space-y-1">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>Total HT</span>
              <span data-testid="quote-total-ht">{totals.ht.toFixed(2)} €</span>
            </div>
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>TVA ({tvaRate}%)</span>
              <span data-testid="quote-total-tva">{totals.tva.toFixed(2)} €</span>
            </div>
            <div className="flex items-center justify-between font-heading font-bold text-xl pt-1 border-t border-white/20">
              <span>Total TTC</span>
              <span data-testid="quote-total-ttc">{totals.ttc.toFixed(2)} €</span>
            </div>
            {totals.acompte > 0 && (
              <div className="flex items-center justify-between text-[11px] text-white/70 pt-1">
                <span>Dont acompte demandé ({acomptePct}%)</span>
                <span>{totals.acompte.toFixed(2)} €</span>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-border p-4 flex gap-2">
          <Button
            variant="outline"
            className="rounded-full flex-1"
            onClick={() => onOpenChange(false)}
            data-testid="quote-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={sending || totals.ttc <= 0}
            className="rounded-full flex-1 bg-pro hover:bg-pro-hover"
            data-testid="quote-submit-btn"
          >
            {sending ? "Envoi…" : "Envoyer le devis"}
          </Button>
        </div>
      </div>
    </div>
  );
}
